import React, { useCallback, useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import StudentDetailsFullUpgrade from "../../StudentDetails/StudentDetailsFullUpgrade";

import BannerCard from "./BannerCard";
import PaymentSummaryCard from "./PaymentSummaryCard";
import { constructPaymentDetails } from "../../../Common/Helpers/PaymentHelper";
import { Money, PaymentStatus, Quote } from "../../../Common/Types";
import WhatsNext from "./WhatsNext/WhatsNext";
import { getQuoteInformation } from "../../../Common/services/QuoteService";
import ContentLayout from "../../BaseComponents/ContentLayout";
import Banner from "./Banner";
import { PostSubmissionCard } from "../../StudentDetails/PostSubmissionCard";
import { usePaymentSummaryContent } from "./usePaymentSummaryContent";

import { Loading } from "@ilc-technology/luik";

interface PaymentSummaryProps {
  quote: Quote;
  reservationPrice?: Money;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({ quote, reservationPrice }) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const { quoteInfo } = getQuoteInformation(quote);
  const [studentDetailsFormComplete, setStudentDetailsFormComplete] = useState<boolean>(false);

  const { isStudentDetailsUpdateEnabled, shouldDisplayPostSubmissionCard, isLoading } = usePaymentSummaryContent(
    quote.id
  );

  const queryStrings = new URLSearchParams(search);
  const referenceId = queryStrings.get("referenceId");
  const paymentStatus = queryStrings.get("status") as PaymentStatus | null;
  const paymentAmount = queryStrings.get("amount") ? Number(queryStrings.get("amount")) : null;
  const paymentDiscount = queryStrings.get("discount") ? Number(queryStrings.get("discount")) : 0;
  const localCurrency = queryStrings.get("localCurrency");

  const timeoutInMinutes = paymentStatus === PaymentStatus.ReservationSuccess ? 60 * 24 : 60;

  const getPaymentDetails = useCallback((paymentStatus: PaymentStatus | null) => {
    return constructPaymentDetails(paymentStatus);
  }, []);

  const paymentDetails = getPaymentDetails(paymentStatus);

  const paid: Money = {
    amount: paymentAmount ?? 0,
    currency: quoteInfo.prices.totalPrice.currency || "",
  };
  const totalAmount = (quoteInfo.prices.totalPrice.amount || 0) - paymentDiscount;

  useEffect(() => {
    const handleTimeout = () => {
      navigate("/", { replace: true });
    };

    const timeoutId = setTimeout(handleTimeout, timeoutInMinutes * 60 * 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const handleStudentDetailsComplete = () => {
    setStudentDetailsFormComplete(true);
  };

  if (isLoading) {
    return <Loading className="loading animate-spin" size="lg" />;
  }

  return (
    <ContentLayout
      testId="paymentSummary-page"
      fullSizePanelShownFirst={true}
      banner={<Banner />}
      topStaticCard={
        <BannerCard
          paymentDetails={paymentDetails}
          paid={paid}
          totalAmount={totalAmount}
          referenceId={referenceId}
          showStudentDetailsBtn={isStudentDetailsUpdateEnabled && !studentDetailsFormComplete}
          localCurrency={localCurrency}
        />
      }
      sidePanel={
        <>
          <PaymentSummaryCard
            quote={quoteInfo}
            paymentDiscount={paymentDiscount}
            reservationPrice={reservationPrice}
            paymentDetails={paymentDetails}
            totalAmount={totalAmount}
            paid={paid}
          />
          {isStudentDetailsUpdateEnabled && <WhatsNext paymentStatus={paymentStatus} />}
        </>
      }
    >
      {isStudentDetailsUpdateEnabled && (
        <StudentDetailsFullUpgrade
          paymentDetails={paymentDetails}
          quote={quoteInfo}
          onComplete={handleStudentDetailsComplete}
        />
      )}
      {shouldDisplayPostSubmissionCard && (!isStudentDetailsUpdateEnabled || studentDetailsFormComplete) && (
        <PostSubmissionCard />
      )}
    </ContentLayout>
  );
};

export default PaymentSummary;
